import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { foodItems as mockFoodItems } from "@/data/mockData";
import { type FoodItem } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Search, Trash2, RotateCcw, Link as LinkIcon, ArrowLeft, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { clientApi, Client, dietPlanApi, DietPlan, foodItemApi } from "@/services/api";

interface MealFood {
  food: FoodItem;
  quantity: number;
  customName?: string;
}

interface MealFoodGroup {
  id: string;
  foods: MealFood[];
  isReplacement?: boolean;
}

interface Meal {
  id: string;
  name: string;
  foodGroups: MealFoodGroup[];
}

interface Day {
  id: string;
  name: string;
  meals: Meal[];
  isLinked: boolean;
}

const WEEKDAY_NAMES = [
  'Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday',
];

const DEFAULT_MEAL_NAMES = ['Breakfast', 'Lunch', 'Dinner'];

const makeDefaultMeals = (): Meal[] => {
  const stamp = Date.now();
  return DEFAULT_MEAL_NAMES.map((name, i) => ({
    id: `meal-${i}-${stamp}-${Math.random().toString(36).slice(2, 8)}`,
    name,
    foodGroups: [
      {
        id: `main-group-${i}-${stamp}-${Math.random().toString(36).slice(2, 8)}`,
        foods: [],
        isReplacement: false,
      },
    ],
  }));
};

const makeDefaultWeekDays = (): Day[] =>
  WEEKDAY_NAMES.map((name, i) => ({
    id: `day-${i}-${Date.now()}-${Math.random()}`,
    name,
    meals: i === 0 ? makeDefaultMeals() : [],
    isLinked: i !== 0,
  }));

const DietPlanEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const containsArabic = (text: string) => /[\u0600-\u06FF]/.test(text);
  const needsRTL = (text: string) => containsArabic(text) || (/^\d/.test(text) && containsArabic(text));

  const sanitizeFoodName = (name: string) => {
    return name.replace(/,/g, '-');
  };

  const calculateNutritionalValue = (baseValue: string | number | undefined, quantity: number): number => {
    if (baseValue === undefined) return 0;
    const numericValue = typeof baseValue === 'string' ? parseFloat(baseValue) : baseValue;
    return (numericValue * quantity) / 100;
  };

  const [selectedClient, setSelectedClient] = useState(searchParams.get('client') || "");
  const [foodItems, setFoodItems] = useState<FoodItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState<Day[]>([]);
  const [selectedDayId, setSelectedDayId] = useState<string | null>(null);
  const [planName, setPlanName] = useState("");
  const [mealSearchQuery, setMealSearchQuery] = useState("");
  const [selectedFoodDetails, setSelectedFoodDetails] = useState<FoodItem | null>(null);
  const [foodQuantity, setFoodQuantity] = useState(100);
  const [selectedMealId, setSelectedMealId] = useState("");
  const [clients, setClients] = useState<Client[]>([]);
  const [clientPlans, setClientPlans] = useState<DietPlan[]>([]);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(searchParams.get('plan') || null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const [showCustomNameDialog, setShowCustomNameDialog] = useState(false);
  const [pendingFoodToAdd, setPendingFoodToAdd] = useState<{food: FoodItem, mealId: string} | null>(null);
  const [customNameInput, setCustomNameInput] = useState("");

  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  // Copy-to-another-client dialog
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copyTargetClientId, setCopyTargetClientId] = useState("");
  const [copyPlanName, setCopyPlanName] = useState("");
  const [copying, setCopying] = useState(false);

  // Delete-current-plan dialog (e.g. a copy made for the wrong client)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Marking a plan as the single active one shown to the client
  const [settingActive, setSettingActive] = useState(false);

  const [clientNutritionGoals, setClientNutritionGoals] = useState<{
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
  } | null>(null);

  const [currentNutrition, setCurrentNutrition] = useState({
    calories: 0,
    protein: 0,
    carbs: 0,
    fats: 0
  });

  const selectedDay = days.find(d => d.id === selectedDayId) ?? null;
  const templateDay = days[0] ?? null;
  const isSelectedDayTemplate = !!(selectedDay && templateDay && selectedDay.id === templateDay.id);
  const isSelectedDayLinked = !!(selectedDay && selectedDay.isLinked && templateDay && !isSelectedDayTemplate);
  const meals: Meal[] = isSelectedDayLinked && templateDay
    ? templateDay.meals
    : (selectedDay?.meals ?? []);

  const cloneMeals = (src: Meal[]): Meal[] => src.map(m => ({
    id: `meal-clone-${Date.now()}-${Math.random()}`,
    name: m.name,
    foodGroups: m.foodGroups.map(g => ({
      id: `group-clone-${Date.now()}-${Math.random()}`,
      isReplacement: g.isReplacement,
      foods: g.foods.map(f => ({
        food: { ...f.food },
        quantity: f.quantity,
        customName: f.customName,
      })),
    })),
  }));

  const setMealsForSelectedDay = (next: Meal[] | ((current: Meal[]) => Meal[])) => {
    if (!selectedDayId) return;
    setDays(prev => {
      const template = prev[0];
      return prev.map(d => {
        if (d.id !== selectedDayId) return d;
        const isLinkedDay = !!d.isLinked && template && d.id !== template.id;
        const baseMeals = isLinkedDay ? cloneMeals(template.meals) : d.meals;
        const computed = typeof next === 'function' ? (next as (m: Meal[]) => Meal[])(baseMeals) : next;
        return { ...d, meals: computed, isLinked: false };
      });
    });
  };

  // Load food items and clients on mount
  useEffect(() => {
    foodItemApi.getAll()
      .then(foods => {
        const mappedFoods = foods.map(food => ({
          _id: food._id,
          name: food.name,
          category: food.category,
          protein: food.protein,
          fat: food.fat,
          energy: food.energy,
          carbohydrate: food.carbohydrate,
          servingSize: food.servingSize || '',
          description: food.description || '',
          isCustom: food.isCustom || false,
          refuse: food.refuse || 0,
          water: food.water || 0,
          ash: food.ash || 0,
          fiber: food.fiber || 0,
          sodium: food.sodium || 0,
          potassium: food.potassium || 0,
          calcium: food.calcium || 0,
          phosphorus: food.phosphorus || 0,
          magnesium: food.magnesium || 0,
          iron: food.iron || 0,
          zinc: food.zinc || 0,
          copper: food.copper || 0,
          vitaminA: food.vitaminA || 0,
          vitaminC: food.vitaminC || 0,
          thiamin: food.thiamin || 0,
          riboflavin: food.riboflavin || 0
        }));
        setFoodItems(mappedFoods);
        setLoading(false);
      })
      .catch(() => {
        const convertedMockFoods = mockFoodItems.map(food => ({
          _id: food.id,
          name: food.name,
          category: food.category,
          protein: food.protein,
          fat: food.fat,
          energy: food.energy,
          carbohydrate: food.carbohydrate,
          servingSize: food.servingSize || '',
          description: '',
          isCustom: false,
          refuse: food.refuse || 0,
          water: food.water || 0,
          ash: food.ash || 0,
          fiber: food.fiber || 0,
          sodium: food.sodium || 0,
          potassium: food.potassium || 0,
          calcium: food.calcium || 0,
          phosphorus: food.phosphorus || 0,
          magnesium: food.magnesium || 0,
          iron: food.iron || 0,
          zinc: Number(food.zinc) || 0,
          copper: food.copper || 0,
          vitaminA: Number(food.vitaminA) || 0,
          vitaminC: Number(food.vitaminC) || 0,
          thiamin: food.thiamin || 0,
          riboflavin: food.riboflavin || 0
        }));
        setFoodItems(convertedMockFoods);
        setLoading(false);
      });

    clientApi.getAll()
      .then(setClients)
      .catch(() => setClients([]));
  }, []);

  const apiMealToUiMeal = (meal: any, idx: number): Meal => {
    const hasReplacements = meal.notes && meal.notes.includes('Replacement options:');
    let replacementGroups: MealFoodGroup[] = [];
    if (hasReplacements) {
      const replacementNotes: string = meal.notes;
      const replacementOptions = replacementNotes.replace('Replacement options: ', '').split('; ');
      replacementGroups = replacementOptions.map((option, groupIdx) => {
        const foods: string[] = [];
        let currentFood = '';
        let parensCount = 0;
        for (let i = 0; i < option.length; i++) {
          const ch = option[i];
          if (ch === '(') parensCount++;
          else if (ch === ')') parensCount = Math.max(0, parensCount - 1);
          else if (ch === ',' && parensCount === 0) {
            if (currentFood.trim()) {
              foods.push(currentFood.trim());
              currentFood = '';
              continue;
            }
          }
          currentFood += ch;
        }
        if (currentFood.trim()) foods.push(currentFood.trim());

        const parsedFoods: MealFood[] = foods.map(foodStr => {
          const match = foodStr.match(/^(.+?)\s*\((\d+)g\)$/);
          if (!match) return null;
          const [, foodName, quantity] = match;
          return {
            food: {
              _id: `replacement-${foodName}-${Date.now()}-${Math.random()}`,
              name: foodName,
              category: "Replacement",
              refuse: 0, water: 0, energy: 0, protein: 0, fat: 0, ash: 0,
              fiber: 0, carbohydrate: 0, sodium: 0, potassium: 0, calcium: 0,
              phosphorus: 0, magnesium: 0, iron: 0, zinc: 0, copper: 0,
              vitaminA: 0, vitaminC: 0, thiamin: 0, riboflavin: 0,
              servingSize: '', description: '', isCustom: false,
            } as FoodItem,
            quantity: parseInt(quantity),
            customName: foodName,
          };
        }).filter(Boolean) as MealFood[];

        return {
          id: `replacement-group-${groupIdx}-${Date.now()}-${Math.random()}`,
          foods: parsedFoods,
          isReplacement: true,
        };
      });
    }

    const mainGroup: MealFoodGroup = {
      id: `main-group-${Date.now()}-${Math.random()}`,
      foods: (meal.foods ?? []).map((food: any) => {
        let customName: string | undefined;
        if (food.notes && food.notes.startsWith('Custom name: ')) {
          customName = food.notes.replace('Custom name: ', '');
        }
        return {
          food: {
            _id: food.name + Math.random(),
            name: food.name,
            category: food.category || "",
            refuse: 0, water: 0,
            energy: food.calories || 0,
            protein: food.protein || 0,
            fat: food.fat || 0,
            ash: 0,
            fiber: food.fiber || 0,
            carbohydrate: food.carbs || 0,
            sodium: food.sodium || 0,
            potassium: food.potassium || 0,
            calcium: food.calcium || 0,
            phosphorus: 0, magnesium: 0,
            iron: food.iron || 0,
            zinc: 0, copper: 0, vitaminA: 0, vitaminC: 0, thiamin: 0, riboflavin: 0,
            servingSize: '', description: '', isCustom: false,
          } as FoodItem,
          quantity: food.quantity,
          customName,
        };
      }),
      isReplacement: false,
    };

    return {
      id: `meal-${idx}-${Date.now()}-${Math.random()}`,
      name: meal.name,
      foodGroups: [mainGroup, ...replacementGroups],
    };
  };

  // Apply an already-fetched plan to the editor (no network call).
  const loadPlanIntoEditor = (plan: DietPlan) => {
    setEditingPlanId(plan._id || null);
    setPlanName(plan.name || "");

    let loadedDays: Day[];
    if (plan.days && plan.days.length > 0) {
      loadedDays = plan.days.map((d, dIdx) => ({
        id: `day-${dIdx}-${Date.now()}-${Math.random()}`,
        name: d.name || `Day ${dIdx + 1}`,
        meals: (d.meals ?? []).map((m, mIdx) => apiMealToUiMeal(m, mIdx)),
        isLinked: dIdx === 0 ? false : (d.isLinked ?? false),
      }));
    } else if (plan.meals && plan.meals.length > 0) {
      loadedDays = [{
        id: `day-0-${Date.now()}-${Math.random()}`,
        name: 'Day 1',
        meals: plan.meals.map((m, mIdx) => apiMealToUiMeal(m, mIdx)),
        isLinked: false,
      }];
    } else {
      loadedDays = [];
    }

    setDays(loadedDays);
    if (loadedDays.length > 0) {
      const firstDay = loadedDays[0];
      setSelectedDayId(firstDay.id);
      if (firstDay.meals.length > 0) {
        setSelectedMealId(firstDay.meals[0].id);
        const firstMain = firstDay.meals[0].foodGroups.find(g => !g.isReplacement);
        if (firstMain) setSelectedGroupId(firstMain.id);
      } else {
        setSelectedMealId("");
      }
    } else {
      setSelectedDayId(null);
      setSelectedMealId("");
    }
  };

  // Reset the editor to a fresh, empty week (new plan).
  const loadFreshPlan = () => {
    setEditingPlanId(null);
    setPlanName("");
    const fresh = makeDefaultWeekDays();
    setDays(fresh);
    const tmpl = fresh[0];
    setSelectedDayId(tmpl.id);
    const firstMeal = tmpl.meals[0];
    if (firstMeal) {
      setSelectedMealId(firstMeal.id);
      const mainGroup = firstMeal.foodGroups.find(g => !g.isReplacement);
      setSelectedGroupId(mainGroup?.id ?? null);
    } else {
      setSelectedMealId("");
      setSelectedGroupId(null);
    }
  };

  // Fetch this client's plans once whenever the client changes.
  useEffect(() => {
    if (!selectedClient) {
      setClientPlans([]);
      return;
    }
    dietPlanApi.getAll(selectedClient)
      .then(setClientPlans)
      .catch(() => setClientPlans([]));
  }, [selectedClient]);

  // Decide which plan to show and load it from the already-fetched list, so
  // switching clients / plans is instant (no extra network round-trip).
  useEffect(() => {
    if (!selectedClient) return;

    // Explicit "new" or nothing selected → blank plan.
    if (!selectedPlanId || selectedPlanId === 'new') {
      loadFreshPlan();
      return;
    }

    // Prefer the selected plan; if it belongs to a previous client (stale id
    // after switching clients), fall back to this client's most recent plan.
    let plan = clientPlans.find(p => p._id === selectedPlanId) || null;
    if (!plan && clientPlans.length > 0) {
      plan = [...clientPlans].sort((a, b) =>
        new Date(b.updatedAt || b.createdAt || 0).getTime() -
        new Date(a.updatedAt || a.createdAt || 0).getTime()
      )[0];
    }

    if (plan) {
      loadPlanIntoEditor(plan);
      if (plan._id && plan._id !== selectedPlanId) setSelectedPlanId(plan._id);
    } else {
      loadFreshPlan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientPlans, selectedPlanId, selectedClient]);

  // Set nutrition goals when client changes
  useEffect(() => {
    if (selectedClient) {
      clientApi.getById(selectedClient)
        .then(client => {
          const bmrDietPlans = client.diet || [];
          if (bmrDietPlans.length > 0) {
            const sortedPlans = bmrDietPlans.sort((a, b) =>
              new Date(b.date).getTime() - new Date(a.date).getTime()
            );
            const latestPlan = sortedPlans[0];
            setClientNutritionGoals({
              calories: latestPlan.calories || 2000,
              protein: latestPlan.protein || 150,
              carbs: latestPlan.carbs || 200,
              fats: latestPlan.fats || 67
            });
          } else {
            setClientNutritionGoals({
              calories: 2000,
              protein: 150,
              carbs: 200,
              fats: 67
            });
          }
        })
        .catch(() => {
          setClientNutritionGoals({
            calories: 2000,
            protein: 150,
            carbs: 200,
            fats: 67
          });
        });
    } else {
      setClientNutritionGoals(null);
    }
  }, [selectedClient]);

  // Auto-select first food when meal changes
  useEffect(() => {
    if (!selectedMealId) {
      setSelectedFoodDetails(null);
      return;
    }
    const meal = meals.find(m => m.id === selectedMealId);
    if (meal && meal.foodGroups.length > 0) {
      const firstGroup = meal.foodGroups[0];
      const firstFood = firstGroup.foods[0];
      if (firstFood) {
        setSelectedFoodDetails(firstFood.food);
        setFoodQuantity(firstFood.quantity);
      } else {
        setSelectedFoodDetails(null);
      }
    } else {
      setSelectedFoodDetails(null);
    }
  }, [selectedMealId, days, selectedDayId]);

  // Calculate nutrition totals
  useEffect(() => {
    const totals = meals.reduce((acc, meal) => {
      const mainGroup = meal.foodGroups.find(group => !group.isReplacement);
      if (mainGroup) {
        mainGroup.foods.forEach(mealFood => {
          acc.calories += calculateNutritionalValue(mealFood.food.energy, mealFood.quantity);
          acc.protein += calculateNutritionalValue(mealFood.food.protein, mealFood.quantity);
          acc.carbs += calculateNutritionalValue(mealFood.food.carbohydrate, mealFood.quantity);
          acc.fats += calculateNutritionalValue(mealFood.food.fat, mealFood.quantity);
        });
      }
      return acc;
    }, { calories: 0, protein: 0, carbs: 0, fats: 0 });

    setCurrentNutrition({
      calories: Math.round(totals.calories),
      protein: Math.round(totals.protein * 10) / 10,
      carbs: Math.round(totals.carbs * 10) / 10,
      fats: Math.round(totals.fats * 10) / 10
    });
  }, [days, selectedDayId]);

  const handleAddDay = () => {
    const hasTemplate = days.length > 0;
    const newDay: Day = {
      id: `day-${Date.now()}-${Math.random()}`,
      name: `Day ${days.length + 1}`,
      meals: [],
      isLinked: hasTemplate,
    };
    setDays(prev => [...prev, newDay]);
    setSelectedDayId(newDay.id);
    setSelectedMealId("");
    setSelectedGroupId(null);
    setSelectedFoodDetails(null);
  };

  const handleRemoveDay = (dayId: string) => {
    const removingTemplate = days.length > 0 && days[0].id === dayId;
    setDays(prev => {
      const filtered = prev.filter(d => d.id !== dayId);
      if (removingTemplate && filtered.length > 0) {
        const oldTemplateMeals = prev[0].meals;
        return filtered.map((d, i) => {
          if (i !== 0) return d;
          const adoptedMeals = d.isLinked ? cloneMeals(oldTemplateMeals) : d.meals;
          return { ...d, meals: adoptedMeals, isLinked: false };
        });
      }
      return filtered;
    });
    if (selectedDayId === dayId) {
      const remaining = days.filter(d => d.id !== dayId);
      const next = remaining[0] ?? null;
      setSelectedDayId(next ? next.id : null);
      setSelectedMealId("");
      setSelectedGroupId(null);
      setSelectedFoodDetails(null);
    }
  };

  const handleRenameDay = (dayId: string, name: string) => {
    setDays(prev => prev.map(d => (d.id === dayId ? { ...d, name } : d)));
  };

  const handleRelinkDay = (dayId: string) => {
    setDays(prev => {
      if (prev.length === 0 || prev[0].id === dayId) return prev;
      return prev.map(d => (d.id === dayId ? { ...d, meals: [], isLinked: true } : d));
    });
    if (selectedDayId === dayId) {
      setSelectedMealId("");
      setSelectedGroupId(null);
      setSelectedFoodDetails(null);
    }
  };

  const handleSelectDay = (dayId: string) => {
    setSelectedDayId(dayId);
    const day = days.find(d => d.id === dayId);
    const firstMeal = day?.meals[0];
    if (firstMeal) {
      setSelectedMealId(firstMeal.id);
      const main = firstMeal.foodGroups.find(g => !g.isReplacement);
      setSelectedGroupId(main?.id ?? null);
    } else {
      setSelectedMealId("");
      setSelectedGroupId(null);
    }
    setSelectedFoodDetails(null);
  };

  const handleAddMeal = () => {
    const newMeal: Meal = {
      id: `meal-${Date.now()}`,
      name: `Meal ${meals.length + 1}`,
      foodGroups: [
        {
          id: `main-group-${Date.now()}`,
          foods: [],
          isReplacement: false
        }
      ]
    };
    if (!selectedDayId) {
      toast({
        title: "No Day Selected",
        description: "Please select a day before adding a meal.",
        variant: "destructive"
      });
      return;
    }
    setMealsForSelectedDay(prevMeals => [...prevMeals, newMeal]);
    setSelectedMealId(newMeal.id);
    setSelectedFoodDetails(null);
    setFoodQuantity(100);
    setSelectedGroupId(newMeal.foodGroups[0].id);
  };

  const handleRemoveMeal = (mealId: string) => {
    const remaining = meals.filter(meal => meal.id !== mealId);
    setMealsForSelectedDay(remaining);
    if (selectedMealId === mealId) {
      const next = remaining[0];
      setSelectedMealId(next?.id ?? "");
      const main = next?.foodGroups.find(g => !g.isReplacement);
      setSelectedGroupId(main?.id ?? null);
    }
  };

  const handleAddFoodToMeal = (mealId: string, food: FoodItem) => {
    if (!mealId || !food) return;

    if (!selectedGroupId) {
      const meal = meals.find(m => m.id === mealId);
      if (meal && meal.foodGroups.length === 0) {
        const newMainGroupId = `main-group-${Date.now()}-${Math.random()}`;
        setMealsForSelectedDay(prev => prev.map(m =>
          m.id === mealId
            ? { ...m, foodGroups: [{ id: newMainGroupId, foods: [], isReplacement: false }] }
            : m
        ));
        setSelectedGroupId(newMainGroupId);
        addFoodToMeal(mealId, food, food.name);
        return;
      } else {
        toast({
          title: "No Group Selected",
          description: "Please select a group (Main or Replacement) before adding food.",
          variant: "destructive"
        });
        return;
      }
    }

    const meal = meals.find(m => m.id === mealId);
    const group = meal?.foodGroups.find(g => g.id === selectedGroupId);

    if (group?.isReplacement) {
      setPendingFoodToAdd({ food, mealId });
      setCustomNameInput(food.name);
      setShowCustomNameDialog(true);
    } else {
      addFoodToMeal(mealId, food, food.name);
    }
  };

  const addFoodToMeal = (mealId: string, food: FoodItem, customName?: string) => {
    const sanitizedCustomName = customName ? sanitizeFoodName(customName) : undefined;
    const sanitizedFood = { ...food, name: sanitizeFoodName(food.name) };

    setMealsForSelectedDay(prev => prev.map(m => {
      if (m.id !== mealId) return m;
      const groupIdx = m.foodGroups.findIndex(g => g.id === selectedGroupId);
      if (groupIdx === -1) return m;
      const group = m.foodGroups[groupIdx];
      if (group.foods.some(mealFood => mealFood.food._id === food._id)) {
        toast({
          title: "Food already added",
          description: "This food is already in the selected group. You can adjust its quantity instead.",
          variant: "destructive"
        });
        return m;
      }
      const mealFood = { food: sanitizedFood, quantity: foodQuantity, customName: sanitizedCustomName };
      const updatedGroups = m.foodGroups.map((g, idx) =>
        idx === groupIdx
          ? { ...g, foods: [...g.foods, mealFood] }
          : g
      );
      return { ...m, foodGroups: updatedGroups };
    }));
  };

  const handleUpdateFoodQuantity = (mealId: string, foodId: string, quantity: number) => {
    setMealsForSelectedDay(prev => prev.map(meal => {
      if (meal.id === mealId) {
        return {
          ...meal,
          foodGroups: meal.foodGroups.map(group => ({
            ...group,
            foods: group.foods.map(foodItem => {
              if (foodItem.food._id === foodId) {
                return { ...foodItem, quantity };
              }
              return foodItem;
            })
          }))
        };
      }
      return meal;
    }));
  };

  const handleRemoveFoodFromMeal = (mealId: string, foodId: string) => {
    setMealsForSelectedDay(prev => prev.map(meal => {
      if (meal.id === mealId) {
        return {
          ...meal,
          foodGroups: meal.foodGroups.map(group => ({
            ...group,
            foods: group.foods.filter(foodItem => foodItem.food._id !== foodId)
          }))
        };
      }
      return meal;
    }));
  };

  const handleUpdateFoodName = (mealId: string, foodId: string, newName: string) => {
    const sanitizedName = sanitizeFoodName(newName);
    setMealsForSelectedDay(prev => prev.map(meal => {
      if (meal.id === mealId) {
        return {
          ...meal,
          foodGroups: meal.foodGroups.map(group => ({
            ...group,
            foods: group.foods.map(foodItem => {
              if (foodItem.food._id === foodId) {
                return {
                  ...foodItem,
                  customName: sanitizedName,
                  food: { ...foodItem.food, name: sanitizedName }
                };
              }
              return foodItem;
            })
          }))
        };
      }
      return meal;
    }));
  };

  const uiMealToApiMeal = (meal: Meal) => {
    const mainGroup = meal.foodGroups.find(group => !group.isReplacement);
    const replacementGroups = meal.foodGroups.filter(group => group.isReplacement);

    let replacementNotes = '';
    if (replacementGroups.length > 0) {
      replacementNotes = 'Replacement options: ' + replacementGroups.map(group =>
        group.foods.map(f => `${sanitizeFoodName(f.customName || f.food.name)} (${f.quantity}g)`).join(', ')
      ).join('; ');
    }

    return {
      name: meal.name,
      foods: mainGroup ? mainGroup.foods.map(f => ({
        name: sanitizeFoodName(f.customName || f.food.name),
        category: f.food.category,
        calories: f.food.energy,
        protein: f.food.protein,
        carbs: f.food.carbohydrate,
        fat: f.food.fat,
        fiber: f.food.fiber,
        sodium: f.food.sodium,
        potassium: f.food.potassium,
        calcium: f.food.calcium,
        iron: f.food.iron,
        quantity: f.quantity,
        unit: 'g',
        notes: f.customName ? `Custom name: ${sanitizeFoodName(f.customName)}` : undefined,
      })) : [],
      notes: replacementNotes || undefined,
    };
  };

  const handleSavePlan = async () => {
    const totalMeals = days.reduce((sum, d) => sum + d.meals.length, 0);
    if (!planName || !selectedClient || days.length === 0 || totalMeals === 0) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields and add at least one day with one meal.",
        variant: "destructive",
      });
      return;
    }
    try {
      const duplicate = clientPlans.find(p => p.name === planName && p._id !== editingPlanId);
      if (duplicate) {
        toast({
          title: "Duplicate Name",
          description: "A diet plan with this name already exists for this client.",
          variant: "destructive",
        });
        return;
      }

      const planData = {
        client: selectedClient,
        name: planName,
        days: days.map((d, i) => {
          const isTemplate = i === 0;
          const linked = !isTemplate && d.isLinked;
          return {
            name: d.name,
            meals: linked ? [] : d.meals.map(uiMealToApiMeal),
            isLinked: !isTemplate && !!d.isLinked,
          };
        }),
        meals: [],
      };

      if (editingPlanId) {
        await dietPlanApi.update(editingPlanId, planData);
      } else {
        await dietPlanApi.create(planData);
      }
      toast({
        title: editingPlanId ? "Diet Plan Updated" : "Diet Plan Created",
        description: editingPlanId ? "Diet plan has been updated successfully." : "New diet plan has been created successfully.",
        variant: "default",
      });
      navigate('/diets');
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to save diet plan.",
        variant: "destructive",
      });
    }
  };

  // Build the API `days` payload from the current editor state. Shared by save
  // and copy so the copy is a faithful, fully-editable duplicate.
  const buildDaysPayload = () =>
    days.map((d, i) => {
      const isTemplate = i === 0;
      const linked = !isTemplate && d.isLinked;
      return {
        name: d.name,
        meals: linked ? [] : d.meals.map(uiMealToApiMeal),
        isLinked: !isTemplate && !!d.isLinked,
      };
    });

  const openCopyDialog = () => {
    setCopyTargetClientId("");
    setCopyPlanName(planName ? `${planName} (Copy)` : "");
    setShowCopyDialog(true);
  };

  const handleCopyPlan = async () => {
    const totalMealsToCopy = days.reduce((sum, d) => sum + d.meals.length, 0);
    if (days.length === 0 || totalMealsToCopy === 0) {
      toast({
        title: "Nothing to copy",
        description: "Add at least one day with a meal before copying.",
        variant: "destructive",
      });
      return;
    }
    if (!copyTargetClientId) {
      toast({
        title: "Select a client",
        description: "Choose a client to copy this plan to.",
        variant: "destructive",
      });
      return;
    }
    if (!copyPlanName.trim()) {
      toast({
        title: "Plan name required",
        description: "Enter a name for the copied plan.",
        variant: "destructive",
      });
      return;
    }

    setCopying(true);
    try {
      const planData = {
        client: copyTargetClientId,
        name: copyPlanName.trim(),
        days: buildDaysPayload(),
        meals: [],
      };
      await dietPlanApi.create(planData);
      const targetName =
        clients.find(c => c._id === copyTargetClientId)?.name || "the selected client";
      toast({
        title: "Plan Copied",
        description: `A copy was created for ${targetName}. You can open and edit it from their diet plans.`,
        variant: "default",
      });
      setShowCopyDialog(false);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to copy diet plan. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCopying(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!editingPlanId) return;
    setDeleting(true);
    try {
      await dietPlanApi.delete(editingPlanId);
      setClientPlans(prev => prev.filter(p => p._id !== editingPlanId));
      toast({
        title: "Diet Plan Deleted",
        description: "The diet plan has been deleted successfully.",
      });
      setShowDeleteConfirmation(false);
      setEditingPlanId(null);
      // Reset the editor to a fresh plan for the current client.
      setSelectedPlanId('new');
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to delete diet plan. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  // Make the current plan the single active plan for this client. The client
  // app only shows the active plan; all of this client's other plans are set to
  // "draft" so exactly one stays visible.
  const handleSetActive = async () => {
    if (!editingPlanId) return;
    setSettingActive(true);
    try {
      await dietPlanApi.update(editingPlanId, { status: 'active' } as any);
      const others = clientPlans.filter(
        p => p._id && p._id !== editingPlanId && p.status === 'active'
      );
      await Promise.all(
        others.map(p => dietPlanApi.update(p._id!, { status: 'draft' } as any))
      );
      setClientPlans(prev =>
        prev.map(p =>
          p._id === editingPlanId
            ? { ...p, status: 'active' }
            : p.status === 'active'
              ? { ...p, status: 'draft' }
              : p
        )
      );
      toast({
        title: "Active Plan Set",
        description: "Only this plan will now appear for the client.",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to set the active plan. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSettingActive(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedMealId("");
    setSelectedFoodDetails(null);
    setFoodQuantity(100);
    setMealSearchQuery('');
    setPlanName('');
    setDays([]);
    setSelectedDayId(null);
    setShowCloseConfirmation(false);
    navigate('/diets');
  };

  const handleCloseAttempt = () => {
    const hasAnyMeals = days.some(d => d.meals.length > 0);
    if (planName || hasAnyMeals || days.length > 0) {
      setShowCloseConfirmation(true);
    } else {
      handleCloseModal();
    }
  };

  const handleConfirmCustomName = () => {
    if (pendingFoodToAdd && customNameInput.trim()) {
      const sanitizedCustomName = sanitizeFoodName(customNameInput.trim());
      addFoodToMeal(pendingFoodToAdd.mealId, pendingFoodToAdd.food, sanitizedCustomName);
      setShowCustomNameDialog(false);
      setPendingFoodToAdd(null);
      setCustomNameInput("");
    }
  };

  const totalMeals = days.reduce((s, d) => s + d.meals.length, 0);
  const incomplete = !planName || !selectedClient || days.length === 0 || totalMeals === 0;

  // Active-plan state for the currently loaded (saved) plan.
  const currentPlan = editingPlanId ? clientPlans.find(p => p._id === editingPlanId) : null;
  const activePlanCount = clientPlans.filter(p => p.status === 'active').length;
  const isCurrentActive = currentPlan?.status === 'active';
  // It's the *sole* active plan only when no other plan is also active.
  const isSoleActive = isCurrentActive && activePlanCount === 1;
  const incompleteHint = !selectedClient ? 'Select a client'
    : !planName ? 'Enter a plan name'
    : days.length === 0 ? 'Add at least one day'
    : totalMeals === 0 ? 'Add at least one meal' : null;

  return (
    <>
      <div className="min-h-screen md:h-screen bg-gray-100 flex flex-col gap-3 p-4 overflow-y-auto md:overflow-hidden">
        {/* ───────── Sticky header (action bar + plan info card) ───────── */}
        <div className="sticky top-0 z-10 bg-gray-100 flex flex-col gap-3 flex-shrink-0 -mx-4 px-4 -mt-4 pt-2 pb-4">
          {/* ───────── Top action bar ───────── */}
          <div className="flex items-center justify-between gap-3 flex-wrap flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <Button variant="ghost" size="sm" onClick={handleCloseAttempt} className="text-gray-700 hover:bg-gray-200/60">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to plans
              </Button>
              <h1 className="text-xl font-bold tracking-tight leading-tight truncate">
                {editingPlanId ? 'Edit Diet Plan' : 'New Diet Plan'}
              </h1>
              {selectedClient && clients.find(c => c._id === selectedClient)?.name && (
                <span className="text-sm text-muted-foreground hidden lg:inline">
                  · {clients.find(c => c._id === selectedClient)?.name}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {incompleteHint && (
                <span className="hidden md:inline text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
                  ⚠ {incompleteHint}
                </span>
              )}
              <Button variant="ghost" size="sm" onClick={handleCloseAttempt} className="text-gray-700 hover:bg-gray-200/60">
                Cancel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={openCopyDialog}
                disabled={totalMeals === 0}
                className="border-green-600 text-green-700 hover:bg-green-50"
                title="Copy this plan to another client"
              >
                <Copy className="h-4 w-4 mr-1" /> Copy
              </Button>
              {editingPlanId && (
                isSoleActive ? (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-md"
                    title="This is the plan your client currently sees"
                  >
                    <CheckCircle2 className="h-4 w-4" /> Active for client
                  </span>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSetActive}
                    disabled={settingActive}
                    className="border-green-600 text-green-700 hover:bg-green-50"
                    title="Make this the only plan the client sees"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    {settingActive ? 'Setting…' : 'Set as Active'}
                  </Button>
                )
              )}
              {editingPlanId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirmation(true)}
                  className="border-red-600 text-red-700 hover:bg-red-50"
                  title="Delete this diet plan"
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleSavePlan}
                disabled={incomplete}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {editingPlanId ? 'Update Plan' : 'Save Plan'}
              </Button>
            </div>
          </div>

          {/* ───────── Plan info card (CLIENT / DIET PLAN / PLAN NAME) ───────── */}
          <div className="bg-white border border-gray-200 rounded-lg px-5 py-4 flex-shrink-0 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold tracking-wider text-gray-500 uppercase">Client</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Choose client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client._id} value={client._id!}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold tracking-wider text-gray-500 uppercase">Diet Plan</Label>
                <Select value={selectedPlanId || 'new'} onValueChange={val => setSelectedPlanId(val)}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Select diet plan or create new" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">+ New Diet Plan</SelectItem>
                    {clientPlans.map(plan => (
                      <SelectItem key={plan._id} value={plan._id!}>
                        {plan.name}{plan.status === 'active' ? ' • Active' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold tracking-wider text-gray-500 uppercase">Plan Name</Label>
                <Input
                  placeholder="e.g. Cutting plan — week 1"
                  value={planName}
                  onChange={(e) => setPlanName(e.target.value)}
                  className="h-10"
                />
              </div>
            </div>

            {/* Nutrition bars */}
            {clientNutritionGoals && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 mt-3 border-t">
                {[
                  { label: 'Calories', cur: currentNutrition.calories, goal: clientNutritionGoals.calories, unit: '', barCls: 'bg-green-500', txtCls: 'text-green-600' },
                  { label: 'Protein', cur: currentNutrition.protein, goal: clientNutritionGoals.protein, unit: 'g', barCls: 'bg-purple-500', txtCls: 'text-purple-600' },
                  { label: 'Carbs', cur: currentNutrition.carbs, goal: clientNutritionGoals.carbs, unit: 'g', barCls: 'bg-yellow-500', txtCls: 'text-yellow-600' },
                  { label: 'Fats', cur: currentNutrition.fats, goal: clientNutritionGoals.fats, unit: 'g', barCls: 'bg-pink-500', txtCls: 'text-pink-600' },
                ].map((b) => (
                  <div key={b.label} className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-[11px] font-medium text-gray-700">{b.label}</span>
                      <span className={`text-[11px] font-bold ${b.txtCls}`}>
                        {b.cur}{b.unit} / {b.goal}{b.unit}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`${b.barCls} h-1.5 rounded-full transition-all duration-300`}
                        style={{ width: `${Math.min((b.cur / (b.goal || 1)) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ───────── Body: 3 columns (Days+Meals | Meal builder | Food picker) ───────── */}
        <div className="flex-1 md:min-h-0">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:h-full">
            {/* ───── LEFT: Days + Meals navigation ───── */}
            <div className="md:col-span-3 flex flex-col gap-3 md:min-h-0 md:overflow-hidden">

              {/* Days */}
              <Card className="flex flex-col flex-shrink-0 md:flex-1 md:min-h-0 md:overflow-hidden">
                <CardHeader className="py-3 px-3">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-[12px] font-bold tracking-wider text-gray-900 uppercase">Days</CardTitle>
                    <Button onClick={handleAddDay} size="sm" variant="ghost" className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50 font-semibold">
                      <Plus className="mr-0.5 h-4 w-4" /> Add
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="px-2 pb-2 pt-0 md:flex-1 md:min-h-0 flex flex-col overflow-hidden">
                  <ScrollArea className="h-[260px] md:h-auto md:flex-1">
                    <div className="space-y-1.5 pr-1">
                      {days.length === 0 ? (
                        <div className="text-center text-muted-foreground py-4 text-xs">
                          No days yet. Click "Add" to start.
                        </div>
                      ) : (
                        days.map((day, idx) => {
                          const isTemplate = idx === 0;
                          const isLinked = !isTemplate && day.isLinked;
                          const isCustom = !isTemplate && !day.isLinked;
                          const effectiveMealCount = isLinked
                            ? days[0].meals.length
                            : day.meals.length;
                          const isSelected = selectedDayId === day.id;
                          return (
                            <div
                              key={day.id}
                              className={`p-3 rounded-lg cursor-pointer transition-colors border ${
                                isSelected
                                  ? "bg-green-600 text-white border-green-600 shadow-sm"
                                  : "bg-white hover:bg-gray-50 border-gray-200"
                              }`}
                              onClick={() => handleSelectDay(day.id)}
                            >
                              <div className="flex items-center justify-between gap-1">
                                <Input
                                  value={day.name}
                                  onChange={(e) => handleRenameDay(day.id, e.target.value)}
                                  className={`h-7 bg-transparent border-none focus-visible:ring-0 text-base font-bold px-1 ${isSelected ? 'text-white placeholder:text-white/60' : 'text-gray-900'}`}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div className="flex items-center gap-0.5">
                                  {isCustom && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className={`h-7 w-7 p-0 ${isSelected ? 'text-white/90 hover:bg-white/20 hover:text-white' : ''}`}
                                      title="Reset this day to follow the template"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRelinkDay(day.id);
                                      }}
                                    >
                                      <RotateCcw className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={`h-7 w-7 p-0 ${isSelected ? 'text-white/90 hover:bg-white/20 hover:text-white' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveDay(day.id);
                                    }}
                                  >
                                    <Trash2 className={`h-4 w-4 ${isSelected ? '' : 'text-red-500'}`} />
                                  </Button>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-1.5 text-[10px] px-1 font-bold tracking-wider uppercase">
                                {isTemplate && (
                                  <span className={`px-2 py-0.5 rounded-md ${
                                    isSelected ? "bg-white/20 text-white" : "bg-amber-100 text-amber-800"
                                  }`}>Template</span>
                                )}
                                {isLinked && (
                                  <span className={`px-2 py-0.5 rounded-md ${
                                    isSelected ? "bg-white/20 text-white" : "bg-blue-100 text-blue-800"
                                  }`}>Linked</span>
                                )}
                                {isCustom && (
                                  <span className={`px-2 py-0.5 rounded-md ${
                                    isSelected ? "bg-white/20 text-white" : "bg-purple-100 text-purple-800"
                                  }`}>Custom</span>
                                )}
                                <span className={`normal-case font-medium tracking-normal text-[12px] ${isSelected ? 'text-white/90' : 'text-gray-500'}`}>
                                  {effectiveMealCount} {effectiveMealCount === 1 ? 'meal' : 'meals'}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </ScrollArea>
                  {days.length > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-2 leading-snug px-1">
                      Edit <span className="font-medium">{days[0].name}</span> (template) → all linked days mirror it.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Meals (in selected day) */}
              <Card className="flex flex-col md:flex-1 md:min-h-0 md:overflow-hidden">
                <CardHeader className="py-3 px-3">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-[12px] font-bold tracking-wider text-gray-900 uppercase">
                      Meals
                      {selectedDay && (
                        <span className="ml-1 text-[11px] font-semibold text-gray-400">
                          · {selectedDay.name.toUpperCase()}
                        </span>
                      )}
                    </CardTitle>
                    <Button onClick={handleAddMeal} size="sm" variant="ghost" disabled={!selectedDayId} className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50 disabled:text-gray-400 font-semibold">
                      <Plus className="mr-0.5 h-4 w-4" /> Add
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="px-2 pb-2 pt-0 md:flex-1 md:min-h-0 flex flex-col overflow-hidden">
                  {!selectedDayId && days.length > 0 && (
                    <div className="text-xs text-muted-foreground mb-2 px-1">
                      Select a day to view or add meals.
                    </div>
                  )}
                  <ScrollArea className="h-[280px] md:h-auto md:flex-1">
                    <div className="space-y-1.5 pr-1">
                      {meals.length === 0 ? (
                        <div className="text-center text-muted-foreground py-4 text-xs">
                          No meals yet. Click "Add" to create one.
                        </div>
                      ) : (
                        meals.map((meal) => {
                          const isSelected = selectedMealId === meal.id;
                          return (
                            <div
                              key={meal.id}
                              className={`p-3 rounded-lg cursor-pointer transition-colors border ${
                                isSelected
                                  ? "bg-green-600 text-white border-green-600 shadow-sm"
                                  : "bg-white hover:bg-gray-50 border-gray-200"
                              }`}
                              onClick={() => {
                                setSelectedMealId(meal.id);
                                const m = meals.find(m => m.id === meal.id);
                                const main = m?.foodGroups.find(g => !g.isReplacement);
                                setSelectedGroupId(main?.id ?? null);
                              }}
                            >
                              <div className="flex items-center justify-between gap-1">
                                <Input
                                  value={meal.name}
                                  onChange={(e) => {
                                    setMealsForSelectedDay(prev => prev.map(m =>
                                      m.id === meal.id ? { ...m, name: e.target.value } : m
                                    ));
                                  }}
                                  className={`h-7 bg-transparent border-none focus-visible:ring-0 text-base font-bold px-1 ${isSelected ? 'text-white' : 'text-gray-900'}`}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`h-7 w-7 p-0 ${isSelected ? 'text-white/90 hover:bg-white/20 hover:text-white' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveMeal(meal.id);
                                  }}
                                >
                                  <Trash2 className={`h-4 w-4 ${isSelected ? '' : 'text-red-500'}`} />
                                </Button>
                              </div>
                              <div className={`text-[12px] px-1 mt-1 font-medium ${isSelected ? 'text-white/90' : 'text-gray-500'}`}>
                                {meal.foodGroups.filter(g => !g.isReplacement).length} main · {meal.foodGroups.filter(g => g.isReplacement).length} repl.
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* ───── CENTER: Selected food (top) + meal builder (below) ───── */}
            <div className="md:col-span-6 flex flex-col gap-3 md:min-h-0 md:overflow-y-auto">
              {selectedFoodDetails && (
                <Card className="flex flex-col md:flex-1 md:min-h-0">
                  <CardHeader className="py-4 px-5 flex-shrink-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <CardTitle
                          className="text-lg font-bold leading-tight truncate"
                          dir={needsRTL(selectedFoodDetails.name) ? 'rtl' : undefined}
                          style={needsRTL(selectedFoodDetails.name) ? { unicodeBidi: 'plaintext' as any } : undefined}
                        >
                          {selectedFoodDetails.name}
                        </CardTitle>
                        <CardDescription className="text-xs mt-0.5">{selectedFoodDetails.category}</CardDescription>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAddFoodToMeal(selectedMealId, selectedFoodDetails)}
                        disabled={!selectedMealId || !selectedGroupId}
                        className="bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-300 disabled:text-gray-500 h-9 px-4"
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        {!selectedMealId
                          ? 'Pick a meal'
                          : !selectedGroupId
                          ? 'Pick a group'
                          : `Add to ${meals.find(m => m.id === selectedMealId)?.foodGroups.find(g => g.id === selectedGroupId)?.isReplacement ? 'Replacement' : 'Main'} (${foodQuantity}g)`}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="px-5 pb-4 pt-0 md:flex-1 md:min-h-0 md:overflow-auto space-y-4">
                    {/* Quantity row */}
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="relative">
                        <Input
                          type="number"
                          min="1"
                          value={foodQuantity}
                          onChange={(e) => setFoodQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                          className="h-9 text-sm w-[110px] pr-7"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">g</span>
                      </div>
                      <div className="flex gap-1.5 ml-1">
                        {[50, 100, 200].map(q => {
                          const active = foodQuantity === q;
                          return (
                            <Button
                              key={q}
                              size="sm"
                              variant="ghost"
                              className={`h-9 px-3 text-xs font-medium border ${
                                active
                                  ? 'bg-green-600 text-white border-green-600 hover:bg-green-700 hover:text-white'
                                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                              }`}
                              onClick={() => setFoodQuantity(q)}
                            >
                              {q}g
                            </Button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Detailed micronutrients */}
                    <div className="grid grid-cols-3 gap-x-6 gap-y-2 pt-1">
                      <div className="space-y-1">
                        <h4 className="text-[10px] font-semibold tracking-wider text-gray-500 uppercase border-b pb-1.5">Basics</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between"><span className="text-gray-600">Calories</span><span className="font-semibold">{calculateNutritionalValue(selectedFoodDetails.energy, foodQuantity)} kcal</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Protein</span><span className="font-semibold">{calculateNutritionalValue(selectedFoodDetails.protein, foodQuantity)}g</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Carbs</span><span className="font-semibold">{calculateNutritionalValue(selectedFoodDetails.carbohydrate, foodQuantity)}g</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Fat</span><span className="font-semibold">{calculateNutritionalValue(selectedFoodDetails.fat, foodQuantity)}g</span></div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-[10px] font-semibold tracking-wider text-gray-500 uppercase border-b pb-1.5">Minerals</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between"><span className="text-gray-600">Calcium</span><span className="font-semibold">{calculateNutritionalValue(selectedFoodDetails.calcium, foodQuantity)}mg</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Iron</span><span className="font-semibold">{calculateNutritionalValue(selectedFoodDetails.iron, foodQuantity)}mg</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Magnesium</span><span className="font-semibold">{calculateNutritionalValue(selectedFoodDetails.magnesium, foodQuantity)}mg</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Sodium</span><span className="font-semibold">{calculateNutritionalValue(selectedFoodDetails.sodium, foodQuantity)}mg</span></div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <h4 className="text-[10px] font-semibold tracking-wider text-gray-500 uppercase border-b pb-1.5">Vitamins</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between"><span className="text-gray-600">Vitamin A</span><span className="font-semibold">{calculateNutritionalValue(selectedFoodDetails.vitaminA, foodQuantity)}µg</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Vitamin C</span><span className="font-semibold">{calculateNutritionalValue(selectedFoodDetails.vitaminC, foodQuantity)}mg</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Thiamin</span><span className="font-semibold">{calculateNutritionalValue(selectedFoodDetails.thiamin, foodQuantity)}mg</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Riboflavin</span><span className="font-semibold">{calculateNutritionalValue(selectedFoodDetails.riboflavin, foodQuantity)}mg</span></div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              {/* Day-status banner */}
              {isSelectedDayLinked && templateDay && (
                <div className="text-[12px] flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-50 text-blue-900 border border-blue-200 flex-shrink-0">
                  <LinkIcon className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>
                    Showing <span className="font-semibold">{templateDay.name}</span>'s meals (linked). Editing will customize <span className="font-semibold">{selectedDay?.name}</span> only.
                  </span>
                </div>
              )}
              {isSelectedDayTemplate && days.length > 1 && (
                <div className="text-[12px] flex items-center gap-1.5 px-3 py-2 rounded-md bg-amber-50 text-amber-900 border border-amber-200 flex-shrink-0">
                  <span className="font-semibold tracking-wider uppercase text-[11px]">Template Day</span>
                  <span className="text-amber-800/80">· edits propagate to all linked days.</span>
                </div>
              )}
              <Card className="flex flex-col md:flex-1 md:min-h-0">
                <CardHeader className="py-3 px-4 flex-shrink-0">
                  <div className="flex justify-between items-center gap-2">
                    <div className="min-w-0">
                      <CardTitle className="text-base truncate">
                        {selectedMealId
                          ? meals.find(m => m.id === selectedMealId)?.name || 'Meal builder'
                          : 'Meal builder'}
                      </CardTitle>
                      {selectedMealId && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {!selectedGroupId
                            ? 'Pick a group below to add foods'
                            : (
                              <>
                                Adding to <span className="text-green-600 font-medium">{meals.find(m => m.id === selectedMealId)?.foodGroups.find(g => g.id === selectedGroupId)?.isReplacement ? 'Replacement' : 'Main'} group</span>
                              </>
                            )}
                        </p>
                      )}
                    </div>
                    {selectedMealId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-green-600 hover:text-green-700 hover:bg-green-50 border border-green-200"
                        onClick={() => {
                          const newGroupId = `replacement-group-${Date.now()}-${Math.random()}`;
                          setMealsForSelectedDay(prev => prev.map(m =>
                            m.id === selectedMealId
                              ? { ...m, foodGroups: [...m.foodGroups, { id: newGroupId, foods: [], isReplacement: true }] }
                              : m
                          ));
                          setSelectedGroupId(newGroupId);
                        }}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" /> Replacement Group
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0 md:flex-1 md:min-h-0 md:overflow-auto">
                  {!selectedDayId ? (
                    <div className="text-center text-muted-foreground py-12 text-sm">
                      <div className="font-medium mb-1">No day selected</div>
                      <div className="text-xs">Pick a day on the left to start.</div>
                    </div>
                  ) : !selectedMealId ? (
                    <div className="text-center text-muted-foreground py-12 text-sm">
                      <div className="font-medium mb-1">No meal selected</div>
                      <div className="text-xs">
                        Pick a meal on the left — or click <span className="font-medium">Add</span> in the Meals panel — to start building.
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {meals.find(m => m.id === selectedMealId)?.foodGroups.length === 0 ? (
                        <div className="text-center text-muted-foreground py-8 text-sm border-2 border-dashed rounded-md">
                          No groups yet. Pick a food on the right and add it to auto-create a main group,
                          <br />or click <span className="font-medium">+ Replacement Group</span> above to create one manually.
                        </div>
                      ) : (
                        meals.find(m => m.id === selectedMealId)?.foodGroups.map((group) => {
                          const groupSelected = selectedGroupId === group.id;
                          return (
                            <div
                              key={group.id}
                              className={`rounded-md cursor-pointer transition-colors ${
                                groupSelected
                                  ? 'bg-white border-l-4 border-green-500 shadow-sm'
                                  : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                              }`}
                              onClick={() => setSelectedGroupId(group.id)}
                            >
                              <div className="flex items-center justify-between px-3 pt-2 pb-1">
                                <div className="text-[11px] font-semibold tracking-wider uppercase flex items-center gap-2">
                                  <span className={groupSelected ? 'text-green-600' : 'text-gray-500'}>
                                    {group.isReplacement ? '🔄 Replacement' : '🍽 Main'}
                                  </span>
                                  <span className="text-[10px] text-gray-400 normal-case font-normal tracking-normal">{group.foods.length} foods</span>
                                </div>
                                {group.isReplacement && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMealsForSelectedDay(prev => prev.map(m =>
                                        m.id === selectedMealId
                                          ? { ...m, foodGroups: m.foodGroups.filter(g => g.id !== group.id) }
                                          : m
                                      ));
                                      if (selectedGroupId === group.id) setSelectedGroupId(null);
                                    }}
                                  >
                                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                  </Button>
                                )}
                              </div>
                              {group.foods.length === 0 ? (
                                <div className="px-3 pb-3 text-gray-500 text-xs italic">
                                  {groupSelected
                                    ? 'Empty group. Pick a food from the right panel to add it here.'
                                    : 'Empty group. Click to select, then pick a food on the right.'}
                                </div>
                              ) : (
                                <div className="px-2 pb-2 space-y-1.5">
                                  {group.foods.map((mealFood, foodIdx) => (
                                    <div
                                      key={mealFood.food._id}
                                      className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-3 py-2 hover:border-green-200 transition-colors"
                                    >
                                      <Input
                                        value={mealFood.customName || mealFood.food.name}
                                        onChange={e => handleUpdateFoodName(selectedMealId, mealFood.food._id!, e.target.value)}
                                        className="flex-1 h-8 border-0 focus-visible:ring-0 px-0 text-sm font-medium"
                                        placeholder="Food name"
                                        dir={needsRTL(mealFood.customName || mealFood.food.name) ? 'rtl' : undefined}
                                        style={needsRTL(mealFood.customName || mealFood.food.name) ? { unicodeBidi: 'plaintext' as any } : undefined}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <Input
                                        type="number"
                                        min="1"
                                        value={mealFood.quantity}
                                        onChange={e => handleUpdateFoodQuantity(selectedMealId, mealFood.food._id!, Math.max(1, parseInt(e.target.value) || 1))}
                                        className="w-[64px] h-8 text-right border-0 focus-visible:ring-0 px-1 text-sm font-semibold"
                                        placeholder="g"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <span className="text-[11px] text-muted-foreground uppercase">g</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveFoodFromMeal(selectedMealId, mealFood.food._id!);
                                        }}
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                      {foodIdx < group.foods.length - 1 && group.isReplacement && (
                                        <span className="text-[11px] text-gray-400 ml-1">or</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* ───── RIGHT: Food database ───── */}
            <div className="md:col-span-3 flex flex-col md:min-h-0">
              <Card className="flex flex-col md:flex-1 md:min-h-0">
                <CardHeader className="py-3 px-3 flex-shrink-0">
                  <CardTitle className="text-[11px] font-semibold tracking-wider text-gray-500 uppercase">Food Database</CardTitle>
                  <div className="relative mt-1.5">
                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search foods…"
                      value={mealSearchQuery}
                      onChange={(e) => setMealSearchQuery(e.target.value)}
                      className="h-9 pl-8"
                    />
                  </div>
                </CardHeader>
                <CardContent className="px-2 pb-2 pt-0 md:flex-1 md:min-h-0 flex flex-col overflow-hidden">
                  <ScrollArea className="h-[400px] md:h-auto md:flex-1">
                    <div className="space-y-1 pr-1">
                      {foodItems.length === 0 ? (
                        <div className="text-center text-muted-foreground py-4 text-xs">
                          No foods in database. Add some first.
                        </div>
                      ) : foodItems
                        .filter((food) =>
                          food.name.toLowerCase().includes(mealSearchQuery.toLowerCase()) ||
                          food.category.toLowerCase().includes(mealSearchQuery.toLowerCase())
                        ).length === 0 ? (
                        <div className="text-center text-muted-foreground py-4 text-xs">
                          No foods match "{mealSearchQuery}".
                        </div>
                      ) : (
                        foodItems
                          .filter((food) =>
                            food.name.toLowerCase().includes(mealSearchQuery.toLowerCase()) ||
                            food.category.toLowerCase().includes(mealSearchQuery.toLowerCase())
                          )
                          .map((food) => (
                            <div
                              key={food._id}
                              className={`p-2 rounded-md cursor-pointer transition-colors border ${
                                selectedFoodDetails?._id === food._id
                                  ? "bg-primary text-primary-foreground border-primary"
                                  : "hover:bg-muted border-transparent"
                              }`}
                              onClick={() => {
                                setSelectedFoodDetails(food);
                                setFoodQuantity(100);
                              }}
                            >
                              <div
                                className="text-sm font-medium leading-tight"
                                dir={needsRTL(food.name) ? 'rtl' : undefined}
                                style={needsRTL(food.name) ? { unicodeBidi: 'plaintext' as any } : undefined}
                              >
                                {food.name}
                              </div>
                              <div className="text-[10px] opacity-70">{food.category}</div>
                            </div>
                          ))
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Close confirmation dialog */}
      <Dialog open={showCloseConfirmation} onOpenChange={setShowCloseConfirmation}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirm Close</DialogTitle>
            <DialogDescription>
              Are you sure you want to close? All unsaved changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseConfirmation(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleCloseModal}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete current plan confirmation */}
      <Dialog open={showDeleteConfirmation} onOpenChange={(open) => !deleting && setShowDeleteConfirmation(open)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Diet Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this diet plan? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirmation(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeletePlan} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy to another client dialog */}
      <Dialog open={showCopyDialog} onOpenChange={(open) => !copying && setShowCopyDialog(open)}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Copy Diet Plan to Another Client</DialogTitle>
            <DialogDescription>
              This creates a new, fully editable copy of the current plan — all days, meals
              and foods — for the client you choose. The original plan is not changed.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <Label>Target Client</Label>
              <Select value={copyTargetClientId} onValueChange={setCopyTargetClientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c._id} value={c._id!}>
                      {c.name}{c._id === selectedClient ? ' (current)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>New Plan Name</Label>
              <Input
                placeholder="e.g. Cutting plan — week 1 (Copy)"
                value={copyPlanName}
                onChange={(e) => setCopyPlanName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopyDialog(false)} disabled={copying}>
              Cancel
            </Button>
            <Button
              onClick={handleCopyPlan}
              disabled={copying || !copyTargetClientId || !copyPlanName.trim()}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {copying ? 'Copying…' : 'Create Copy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom name dialog */}
      <Dialog open={showCustomNameDialog} onOpenChange={setShowCustomNameDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Enter Custom Name for Replacement Meal</DialogTitle>
            <DialogDescription>
              Please enter a custom name (e.g., Arabic name) for this replacement food item.
              This name will be displayed in the diet plan instead of the original food name.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="customName" className="text-right">
                Custom Name
              </Label>
              <Input
                id="customName"
                className="col-span-3"
                placeholder="Enter custom name (e.g., Arabic name)"
                value={customNameInput}
                onChange={(e) => setCustomNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmCustomName();
                  }
                }}
              />
            </div>
            {pendingFoodToAdd && (
              <div className="text-xs text-muted-foreground">
                Original food: <strong>{pendingFoodToAdd.food.name}</strong>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomNameDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmCustomName} disabled={!customNameInput.trim()}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DietPlanEditor;
